import { useState, useEffect } from 'react';
import { useLoanTypes } from '../hooks/useLoanTypes.ts';
import { useReapplyLoan, ReapplyLoanData } from '../hooks/useReapplyLoan.ts';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';
import { toast } from 'react-hot-toast';

export default function ReapplyLoanForm({ originalLoan, onSubmit, onCancel }) {
  const { data: loanTypesData, isLoading: loanTypesLoading, error: loanTypesError } = useLoanTypes();
  const reapplyLoan = useReapplyLoan();
  
  const [formData, setFormData] = useState({
    reapply_from_loan_id: originalLoan.id,
    loan_type_id: '',
    principal: originalLoan.loan_balance,
    top_up_amount: 0,
    reapply_reason: '',
    start_date: new Date().toISOString().split('T')[0],
    reason: ''
  });

  const [selectedLoanType, setSelectedLoanType] = useState(null);

  useEffect(() => {
    if (formData.loan_type_id) {
      const loanType = loanTypesData?.data?.find(lt => lt.id === parseInt(formData.loan_type_id));
      setSelectedLoanType(loanType);
    }
  }, [formData.loan_type_id, loanTypesData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.loan_type_id) {
      toast.error('Please select a loan type');
      return;
    }
    
    if (formData.principal < originalLoan.loan_balance) {
      toast.error('Principal amount must be at least the original loan balance');
      return;
    }

    if (formData.top_up_amount <= 0) {
      toast.error('Top-up amount must be greater than 0');
      return;
    }

    try {
      await reapplyLoan.mutateAsync(formData);
      onSubmit();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const totalAmount = originalLoan.loan_balance + formData.top_up_amount;

  // Show loading state while loan types are being fetched
  if (loanTypesLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Reapply Loan</h3>
          <p className="text-sm text-gray-600 mb-6">
            Create a new loan that will first repay the existing loan balance and then provide additional funds.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Original Loan Details</h4>
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <span className="text-gray-600">Loan Number:</span>
              <span className="ml-2 font-medium">{originalLoan.loan_no}</span>
            </div>
            <div>
              <span className="text-gray-600">Original Principal:</span>
              <span className="ml-2 font-medium">{formatCurrency(originalLoan.principal)}</span>
            </div>
            <div>
              <span className="text-gray-600">Current Balance:</span>
              <span className="ml-2 font-medium text-red-600">{formatCurrency(originalLoan.loan_balance)}</span>
            </div>
            <div>
              <span className="text-gray-600">Member:</span>
              <span className="ml-2 font-medium">{originalLoan.member?.name}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading loan types...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if loan types failed to load
  if (loanTypesError) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Reapply Loan</h3>
          <p className="text-sm text-gray-600 mb-6">
            Create a new loan that will first repay the existing loan balance and then provide additional funds.
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Original Loan Details</h4>
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <span className="text-gray-600">Loan Number:</span>
              <span className="ml-2 font-medium">{originalLoan.loan_no}</span>
            </div>
            <div>
              <span className="text-gray-600">Original Principal:</span>
              <span className="ml-2 font-medium">{formatCurrency(originalLoan.principal)}</span>
            </div>
            <div>
              <span className="text-gray-600">Current Balance:</span>
              <span className="ml-2 font-medium text-red-600">{formatCurrency(originalLoan.loan_balance)}</span>
            </div>
            <div>
              <span className="text-gray-600">Member:</span>
              <span className="ml-2 font-medium">{originalLoan.member?.name}</span>
            </div>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Loan Types</h3>
              <p className="text-sm text-red-700 mt-1">
                Failed to load loan types. Please try again or contact support.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-red-700 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Reapply Loan</h3>
        <p className="text-sm text-gray-600 mb-6">
          Create a new loan that will first repay the existing loan balance and then provide additional funds.
        </p>
      </div>

      {/* Original Loan Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Original Loan Details</h4>
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <span className="text-gray-600">Loan Number:</span>
            <span className="ml-2 font-medium">{originalLoan.loan_no}</span>
          </div>
          <div>
            <span className="text-gray-600">Original Principal:</span>
            <span className="ml-2 font-medium">{formatCurrency(originalLoan.principal)}</span>
          </div>
          <div>
            <span className="text-gray-600">Current Balance:</span>
            <span className="ml-2 font-medium text-red-600">{formatCurrency(originalLoan.loan_balance)}</span>
          </div>
          <div>
            <span className="text-gray-600">Member:</span>
            <span className="ml-2 font-medium">{originalLoan.member?.name}</span>
          </div>
        </div>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Note:</span> The original loan will be marked as completed with a "Top-up Loan" repayment record when this reapply loan is created.
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Loan Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Loan Type *
          </label>
          <select
            name="loan_type_id"
            value={formData.loan_type_id}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a loan type</option>
            {(loanTypesData?.data || []).map(loanType => (
              <option key={loanType.id} value={loanType.id}>
                {loanType.name} - {loanType.monthly_interest}% interest
                {loanType.formatted_duration && ` (${loanType.formatted_duration})`}
              </option>
            ))}
          </select>
        </div>



        {/* Top-up Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Top-up Amount *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">KES</span>
            <input
              type="number"
              name="top_up_amount"
              value={formData.top_up_amount}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
              required
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Additional amount beyond the original loan balance
          </p>
        </div>

        {/* Principal Amount (calculated) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Principal Amount
          </label>
          <div className="bg-gray-50 px-3 py-2 rounded-md">
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(totalAmount)}
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Original balance ({formatCurrency(originalLoan.loan_balance)}), Top-up ({formatCurrency(formData.top_up_amount)})
            </p>
          </div>
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date *
          </label>
          <input
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Reapply Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reapply Reason
          </label>
          <textarea
            name="reapply_reason"
            value={formData.reapply_reason}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Reason for reapply loan (optional)"
          />
        </div>

        {/* Loan Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Purpose *
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Purpose of the loan"
            required
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={reapplyLoan.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {reapplyLoan.isPending ? 'Creating...' : 'Create Reapply Loan'}
          </button>
        </div>
      </form>
    </div>
  );
} 